import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CreateExternalEmployeeDocument,
  type ExternalAccessPageItemFragment,
  ExternalAccessesPageQueryDocument,
  InviteAccessDocument,
  UpdateExternalEmployeeDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import AvatarUploader from './AvatarUploader'

interface ExternalUserForm {
  email: string
  firstName: string
  lastName: string
  avatar?: File | null
}

interface Props {
  userId?: number
  onClose: () => void
  onSuccess?: (userId: number) => void
}

const EditUserModal = ({ onClose, userId, onSuccess }: Props) => {
  const { company } = useCurrentSession()
  const { data } = useQuery(ExternalAccessesPageQueryDocument)
  const existingAccess = userId
    ? data?.apiCore.accessesConnection.nodes.find(
        (access: ExternalAccessPageItemFragment) => access.id === userId
      )
    : undefined

  const [createExternalEmployee] = useMutation(CreateExternalEmployeeDocument, {
    extractorFn: (x) => x.apiCoreMutations.createExternalApicoreAccess,
    refetchQueries: [ExternalAccessesPageQueryDocument],
  })

  const [updateExternalEmployee] = useMutation(UpdateExternalEmployeeDocument, {
    extractorFn: (x) => x.apiCoreMutations.updateApicoreAccess,
    refetchQueries: [ExternalAccessesPageQueryDocument],
  })

  const [inviteAccess] = useMutation(InviteAccessDocument, {
    extractorFn: (x) => x.apiCoreMutations.inviteApicoreAccess,
  })

  const isEditing = !!existingAccess

  const form = useForm<ExternalUserForm>({
    defaultValues: {
      email: existingAccess?.email || '',
      firstName: existingAccess?.firstName || '',
      lastName: existingAccess?.lastName || '',
      avatar: null,
    },
    validate(values) {
      const errors: FormErrors<ExternalUserForm> = {}

      if (!values.email) {
        errors.email = i18n.t('common.validations.empty')
      }

      if (!values.firstName) {
        errors.firstName = i18n.t('common.validations.empty')
      }

      if (!values.lastName) {
        errors.lastName = i18n.t('common.validations.empty')
      }

      return errors
    },
    async onSubmit(form_data, { setErrors }) {
      if (isEditing && userId) {
        // Update existing user (only firstName and lastName)
        const result = await updateExternalEmployee({
          variables: {
            id: userId,
            firstName: form_data.firstName,
            lastName: form_data.lastName,
            avatar: form_data.avatar,
          },
        })

        const handled = handleResult(result, setErrors)

        if (handled.success) {
          onClose()
        }

        return handled
      } else {
        // Create new user
        const result = await createExternalEmployee({
          variables: {
            companyId: company.id,
            email: form_data.email,
            firstName: form_data.firstName,
            lastName: form_data.lastName,
            avatar: form_data.avatar,
          },
        })

        const handled = handleResult(result, setErrors)

        if (handled.success && handled.data?.access?.id) {
          // Send invitation for new users
          try {
            await inviteAccess({
              variables: {
                id: handled.data.access.id,
              },
            })
          } catch (inviteError) {
            // If invitation fails, still close the modal as user was created successfully
          }

          if (onSuccess) {
            onSuccess(handled.data.access.id)
          }

          onClose()
        }

        return handled
      }
    },
  })

  const title = isEditing
    ? i18n.t('settings.users.edit_modal.title')
    : i18n.t('settings.users.add_modal.title')

  const description = isEditing
    ? i18n.t('settings.users.edit_modal.description')
    : i18n.t('settings.users.add_modal.description')

  const illustration = isEditing ? 'pencil' : 'add-user'

  return (
    <Modal
      title={title}
      description={description}
      illustration={illustration}
      onSubmit={() => form.submitForm()}
      onClose={onClose}
      footer={
        <Button disabled={form.isSubmitting} submit>
          {i18n.t('common.save')}
        </Button>
      }
    >
      <Box gap="s16">
        <TextField
          label={i18n.t('common.email')}
          value={form.values.email}
          onChange={(value) => form.setFieldValue('email', value)}
          error={form.touched.email ? form.errors.email : undefined}
          readonly={isEditing} // Email cannot be changed when editing
        />
        <Box width="full" alignItems="center">
          <AvatarUploader
            value={form.values.avatar || existingAccess?.avatar || null}
            onChange={(file: File | null) => {
              form.setFieldValue('avatar', file)
            }}
            size="s80"
          />
        </Box>
        <Box flexDirection="row" gap="s16">
          <TextField
            label={i18n.t('fields.first_name')}
            value={form.values.firstName}
            onChange={(value) => form.setFieldValue('firstName', value)}
            error={form.touched.firstName ? form.errors.firstName : undefined}
          />
          <TextField
            label={i18n.t('fields.last_name')}
            value={form.values.lastName}
            onChange={(value) => form.setFieldValue('lastName', value)}
            error={form.touched.lastName ? form.errors.lastName : undefined}
          />
        </Box>
      </Box>
    </Modal>
  )
}

export default EditUserModal
